<script setup lang="ts">
import { nextTick, onMounted, ref, watch } from 'vue';
import Composer from './Composer.vue';
import ConfirmCard from './ConfirmCard.vue';
import ToolCard from './ToolCard.vue';
import { useChat } from '../lib/useChat';

const emit = defineEmits<{ 'open-settings': [] }>();

const chat = useChat();
const scroller = ref<HTMLElement | null>(null);

// The parent keeps this component mounted while the settings screen is open, so a
// key stored there has to clear the block here without remounting.
defineExpose({ onApiKeySaved: chat.clearApiKeyMissing });

/**
 * A ?prefill= parameter seeds the composer — the hook a browser extension or
 * bookmarklet uses to hand over an address from the page you are on.
 *
 * It is stripped from the URL immediately: a delivery address in the address bar
 * ends up in browser history and in any screen share.
 */
const prefill = ref('');

onMounted(() => {
  const params = new URLSearchParams(window.location.search);
  const value = params.get('prefill');
  if (value) {
    prefill.value = value.slice(0, 2000);
    window.history.replaceState({}, '', window.location.pathname);
  }
});

const SUGGESTIONS = [
  'Welke bezorgopties zijn er voor 2132JE 31?',
  'Wat is de status van zending 12345?',
  'Maak een retourlabel voor zending 12345',
];

// Keep the newest content in view as tokens stream in, but leave the user alone if
// they have scrolled up to read something.
watch(
  () => chat.timeline.value,
  async () => {
    const element = scroller.value;
    if (!element) {
      return;
    }
    const atBottom = element.scrollHeight - element.scrollTop - element.clientHeight < 120;
    if (atBottom) {
      await nextTick();
      element.scrollTop = element.scrollHeight;
    }
  },
  { deep: false },
);
</script>

<template>
  <div class="chat">
    <div ref="scroller" class="chat__scroll">
      <div v-if="chat.timeline.value.length === 0" class="empty">
        <h2>Waarmee kan ik helpen?</h2>
        <p>
          Ik kan bezorgopties opzoeken, verzendlabels aanmaken, zendingen traceren en retourlabels
          regelen. Voor het aanmaken van een zending vraag ik altijd eerst je bevestiging.
        </p>
        <div class="empty__suggestions">
          <button
            v-for="suggestion in SUGGESTIONS"
            :key="suggestion"
            class="btn btn--ghost"
            type="button"
            @click="chat.send(suggestion)"
          >
            {{ suggestion }}
          </button>
        </div>
      </div>

      <div
        v-if="chat.apiKeyMissing.value"
        class="notice notice--warn"
      >
        <p>
          Er is nog geen MyParcel API key aan dit account gekoppeld. Stel die eerst in, dan kan ik
          de MyParcel API namens jou gebruiken.
        </p>
        <button class="btn btn--primary" type="button" @click="emit('open-settings')">
          API key instellen
        </button>
      </div>

      <template v-for="item in chat.timeline.value" :key="item.id">
        <div v-if="item.kind === 'user'" class="msg msg--user">{{ item.text }}</div>

        <div v-else-if="item.kind === 'assistant'" class="msg msg--assistant">
          <span class="msg__text">{{ item.text }}</span>
          <span v-if="item.streaming" class="caret" aria-hidden="true"></span>
        </div>

        <ToolCard
          v-else-if="item.kind === 'tool'"
          :name="item.name"
          :status="item.status"
          :text="item.text"
          :shipment-ids="item.shipmentIds"
        />

        <ConfirmCard
          v-else-if="item.kind === 'confirm'"
          :name="item.name"
          :input="item.input"
          :state="item.state"
          :busy="chat.busy.value"
          @approve="chat.approve(item.toolUseId)"
          @decline="(reason) => chat.decline(item.toolUseId, reason)"
        />

        <div v-else-if="item.kind === 'error'" class="notice notice--error">{{ item.text }}</div>
      </template>

      <p v-if="chat.thinking.value" class="thinking">Aan het nadenken…</p>
    </div>

    <Composer
      :busy="chat.busy.value"
      :disabled="chat.apiKeyMissing.value"
      :prefill="prefill"
      @send="chat.send"
      @stop="chat.stop"
    />
  </div>
</template>

<style scoped>
.chat {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
}

.chat__scroll {
  flex: 1;
  overflow-y: auto;
  padding: 20px 16px 8px;
  display: flex;
  flex-direction: column;
}

/*
 * The scroller is a column flex container, so its children would otherwise shrink
 * to share the visible height — collapsing cards to a few pixels while their text
 * overflows. Timeline items must keep their natural height and scroll instead.
 */
.chat__scroll > * {
  flex: 0 0 auto;
}

.empty {
  margin: auto 0;
  text-align: center;
  padding: 24px 8px;
}

.empty h2 {
  font-size: 20px;
  margin: 0 0 8px;
}

.empty p {
  margin: 0 auto 20px;
  max-width: 46ch;
  color: var(--text-muted);
  font-size: 14px;
  line-height: 1.55;
}

.empty__suggestions {
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: center;
}

.msg {
  max-width: 46rem;
  padding: 10px 14px;
  border-radius: 14px;
  font-size: 14px;
  line-height: 1.55;
  white-space: pre-wrap;
  word-break: break-word;
}

.msg--user {
  align-self: flex-end;
  background: var(--accent);
  color: var(--accent-on);
  border-bottom-right-radius: 4px;
  margin: 8px 0;
}

.msg--assistant {
  align-self: flex-start;
  background: var(--surface-muted);
  border-bottom-left-radius: 4px;
  margin: 8px 0;
}

.caret {
  display: inline-block;
  width: 2px;
  height: 1em;
  margin-left: 2px;
  vertical-align: text-bottom;
  background: var(--text);
  animation: blink 1s steps(2, start) infinite;
}

.thinking {
  font-size: 13px;
  color: var(--text-muted);
  margin: 4px 0 8px;
}

@keyframes blink {
  to {
    visibility: hidden;
  }
}

@media (prefers-reduced-motion: reduce) {
  .caret {
    animation: none;
  }
}
</style>
